/**
 * ステータス入力 UI（フェーズ3・要件22-8）。
 *
 * ログイン中のみ表示。「今ここにいる！」の一言をマップのピン吹き出しに出す。
 * Telegram の `/status` と同じデータモデル（Profiles の status_message）を Web から更新する。
 * - 入力して保存 → POST /api/status（message）
 * - 空にして消去 → POST /api/status（空 message で消去）
 * - 開くたびに GET /api/profile で設定中のステータスを取得し、入力欄の初期値と残り時間を出す（タスク40）。
 *   ステータスは3時間で消えるため、ログイン時に取った値ではなく開いた時点の値を使う。
 *   同じ文言のまま「表示する」を押すと、そこから3時間延長される。
 */
import { useState } from 'react';
import { getMyProfile, postStatus } from '../api/client';
import { loadTokens } from './cognito';
import type { UseAuthResult } from './useAuth';
import type { Lang } from '../i18n/lang';
import { messages } from '../i18n/messages';
import { useMenuModal } from './userMenuContext';
import './statusControl.css';

/** ステータス本文の長さ上限（サーバー側 lib/db.ts の STATUS_MAX_CHARS と一致させる）。 */
const STATUS_MAX = 100;

export interface StatusControlProps {
  /** App で一度だけ呼んだ useAuth の結果を渡す。 */
  readonly auth: UseAuthResult;
  /** トリガーボタンの表示形態（menu=メニュー項目／floating=旧フロート）。 */
  readonly variant?: 'menu' | 'floating';
  readonly lang: Lang;
}

export function StatusControl({
  auth,
  variant = 'menu',
  lang,
}: StatusControlProps) {
  const { enabled, user, loading } = auth;
  const loggedIn = enabled && !loading && !!user;
  const m = messages(lang);

  const [open, setOpen] = useMenuModal();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [text, setText] = useState('');
  /** 設定中のステータスを取得中か（取得中は入力・ボタンを無効化）。 */
  const [loadingCurrent, setLoadingCurrent] = useState(false);
  /** 設定中のステータスが消えるまでの残り秒数（開いた時点の値・未設定なら null）。 */
  const [expiresIn, setExpiresIn] = useState<number | null>(null);

  function idToken(): string | null {
    return loadTokens()?.id_token ?? null;
  }

  async function openModal(): Promise<void> {
    setText('');
    setExpiresIn(null);
    setMsg(null);
    setOpen(true);
    const token = idToken();
    if (!token) {
      return;
    }
    setLoadingCurrent(true);
    try {
      const profile = await getMyProfile(token);
      if (profile.status_message) {
        setText(profile.status_message);
        setExpiresIn(profile.status_expires_in ?? null);
      }
    } catch (err) {
      // 取得できなくても空欄から入力できるので、ログだけ残して続ける。
      console.error('status prefill failed', err);
    } finally {
      setLoadingCurrent(false);
    }
  }

  async function save(message: string): Promise<void> {
    const token = idToken();
    if (!token) {
      setMsg(m.status.sessionExpired);
      return;
    }
    if (Array.from(message).length > STATUS_MAX) {
      setMsg(m.status.tooLong(STATUS_MAX));
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await postStatus(token, message);
      setMsg(
        message.trim().length === 0 ? m.status.cleared : m.status.updated,
      );
      setTimeout(() => setOpen(false), 1500);
    } catch (err) {
      // プロフィールが無い（未チェックイン）→ サーバーは 409（タスク55.3）
      if (err instanceof Error && err.message.includes('409')) {
        setMsg(m.status.needCheckin);
      } else {
        setMsg(m.status.failed);
      }
    } finally {
      setBusy(false);
    }
  }

  if (!loggedIn) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className={variant === 'menu' ? 'user-menu__item' : 'status-trigger'}
        onClick={() => void openModal()}
      >
        {m.status.trigger}
      </button>

      {open && (
        <div className="status-modal__overlay" onClick={() => setOpen(false)}>
          <div
            className="status-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={m.status.title}
          >
            <h2 className="status-modal__title">{m.status.title}</h2>
            <p className="status-modal__note">{m.status.note}</p>

            <input
              type="text"
              className="status-modal__input"
              value={text}
              maxLength={STATUS_MAX}
              onChange={(e) => setText(e.target.value)}
              disabled={busy || loadingCurrent}
              placeholder={loadingCurrent ? m.status.loading : m.status.placeholder}
            />
            <span className="status-modal__hint">
              {Array.from(text.trim()).length}/{STATUS_MAX}
            </span>
            {expiresIn !== null && (
              <p className="status-modal__note">{m.status.remaining(expiresIn)}</p>
            )}

            {msg && <p className="status-modal__msg">{msg}</p>}

            <div className="status-modal__actions">
              <button
                type="button"
                className="status-modal__save"
                onClick={() => void save(text)}
                disabled={busy || loadingCurrent || text.trim().length === 0}
              >
                {m.status.show}
              </button>
              <button
                type="button"
                className="status-modal__clear"
                onClick={() => void save('')}
                disabled={busy || loadingCurrent}
              >
                {m.status.clear}
              </button>
            </div>

            <button
              type="button"
              className="status-modal__close"
              onClick={() => setOpen(false)}
            >
              {m.common.close}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
